import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "reactstrap";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import RichTable, { RichColumn } from "src/components/Page/RichTable";
import { useAppDispatch } from "src/store";
import {
  createIncident,
  getStatusOverview,
  setComponentStatus,
  updateIncidentStatus,
} from "src/store/system-status/saga";
import {
  COMPONENT_STATUSES,
  INCIDENT_STATUSES,
  STATUS_COLOR,
  STATUS_LABEL,
  StatusComponent,
  StatusIncident,
  StatusOverview,
} from "src/store/system-status/type";
import { GetLocaleDate } from "src/helpers/getLocaleDate";

/**
 * Sistem durumu — V2 Faz F.
 *
 * Ai admin-panel'indeki status ekranının karşılığı. Okuma ucu anonim
 * (/api/status) — kesinti sırasında durum sayfasına giriş gerekmemeli.
 */
const SystemStatusPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [data, setData] = useState<StatusOverview>();
  const [loading, setLoading] = useState(true);

  document.title = t("SystemStatus.MetaTag", "Sistem Durumu | Rentificial") as string;

  const fetchData = () => {
    setLoading(true);
    dispatch(
      getStatusOverview({
        payload: {
          onSuccess: (_m: any, p: StatusOverview) => {
            setData(p);
            setLoading(false);
          },
          onError: () => setLoading(false),
          onFinally: () => setLoading(false),
        },
      })
    );
  };

  useEffect(fetchData, [dispatch]);

  const changeComponent = (c: StatusComponent, status: string) => {
    if (status === c.Status) return;
    dispatch(
      setComponentStatus({
        id: c.Id,
        body: { Status: status },
        payload: { onSuccess: fetchData, onError: () => {} },
      })
    );
  };

  const openIncident = () => {
    const title = window.prompt(t("SystemStatus.IncidentTitle", "Olay başlığı") as string);
    if (!title || !title.trim()) return;
    const body = window.prompt(t("SystemStatus.IncidentBody", "Açıklama (opsiyonel)") as string) ?? "";
    dispatch(
      createIncident({
        body: {
          Title: title.trim(),
          Body: body.trim() || null,
          Impact: "minor",
          Status: "investigating",
        },
        payload: { onSuccess: fetchData, onError: () => {} },
      })
    );
  };

  const advanceIncident = (i: StatusIncident, status: string) => {
    // Kapanışta not istiyoruz — sonradan "ne olmuştu" sorusunun cevabı kalsın.
    const note =
      status === "resolved"
        ? window.prompt(t("SystemStatus.ResolveNote", "Kapanış notu") as string)
        : null;
    dispatch(
      updateIncidentStatus({
        id: i.Id,
        body: { Status: status, Body: note || null },
        payload: { onSuccess: fetchData, onError: () => {} },
      })
    );
  };

  const componentColumns: RichColumn<StatusComponent>[] = [
    { key: "Name", label: t("SystemStatus.Component", "Bileşen") },
    { key: "Key", label: t("SystemStatus.Key", "Anahtar") },
    {
      key: "Status",
      label: t("SystemStatus.Status", "Durum"),
      render: (r) => (
        <select
          className={`form-select form-select-sm border-${STATUS_COLOR[r.Status] ?? "secondary"}`}
          style={{ width: 165 }}
          value={r.Status}
          onChange={(e) => changeComponent(r, e.target.value)}
        >
          {COMPONENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s] ?? s}
            </option>
          ))}
        </select>
      ),
    },
  ];

  const incidentColumns: RichColumn<StatusIncident>[] = [
    { key: "Title", label: t("SystemStatus.Incident", "Olay") },
    { key: "ComponentName", label: t("SystemStatus.Component", "Bileşen") },
    {
      key: "Status",
      label: t("SystemStatus.Status", "Durum"),
      render: (r) => (
        <span className={`badge bg-${r.ResolvedAt ? "success" : "warning"}`}>{r.Status}</span>
      ),
    },
    {
      key: "StartedAt",
      label: t("SystemStatus.Started", "Başlangıç"),
      render: (r) => GetLocaleDate(r.StartedAt),
    },
    {
      key: "ResolvedAt",
      label: t("SystemStatus.Resolved", "Kapanış"),
      render: (r) => (r.ResolvedAt ? GetLocaleDate(r.ResolvedAt) : "—"),
    },
  ];

  const overall = data?.OverallStatus ?? "operational";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Breadcrumbs
              title={t("Breadcrumb.Dashboard")}
              breadcrumbItem={t("SystemStatus.PageTitle", "Sistem Durumu") as string}
            />
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-success text-light" onClick={openIncident}>
                <i className="bx bx-plus" /> {t("SystemStatus.NewIncident", "Olay aç")}
              </button>
              <button className="btn btn-warning" onClick={fetchData}>
                <i className="mdi mdi-refresh" />
              </button>
            </div>
          </div>

          <div className={`alert alert-${STATUS_COLOR[overall] ?? "secondary"} py-2`}>
            <strong>{t("SystemStatus.Overall", "Genel durum")}:</strong>{" "}
            {STATUS_LABEL[overall] ?? overall}
            {overall !== "operational" && (
              <span className="ms-2 text-muted">
                {t("SystemStatus.WorstHint", "En kötü bileşene göre hesaplanır.")}
              </span>
            )}
          </div>

          <h5 className="mt-4">{t("SystemStatus.Components", "Bileşenler")}</h5>
          <RichTable<StatusComponent>
            tableId="status-components"
            t={t as any}
            columns={componentColumns}
            rows={data?.Components ?? []}
            loading={loading}
            rowKey={(r) => r.Id}
            emptyText={t("SystemStatus.NoComponents", "Bileşen tanımlı değil") as string}
          />

          <h5 className="mt-4">{t("SystemStatus.Incidents", "Olaylar")}</h5>
          <RichTable<StatusIncident>
            tableId="status-incidents"
            t={t as any}
            columns={incidentColumns}
            rows={data?.Incidents ?? []}
            loading={loading}
            rowKey={(r) => r.Id}
            emptyText={t("SystemStatus.NoIncidents", "Son 30 günde olay yok") as string}
            actions={(r) =>
              r.ResolvedAt ? (
                <span className="text-muted">{t("SystemStatus.Closed", "kapalı")}</span>
              ) : (
                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={r.Status}
                  onChange={(e) => advanceIncident(r, e.target.value)}
                >
                  {INCIDENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )
            }
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SystemStatusPage;
